/**
 * BETUP Feed channels — `/api/dashboard/marketing/feed/{channels,channel-categories}`, proxied to v7 admin
 * `feed/channels` / `feed/channel-categories` (betup-server-api docs/feed-channels.md §1 "Localization, categories,
 * sports, linked player" and §4). A channel is a non-player author (an agent) that posts `AGENT_POST` cards players can
 * subscribe to. Dates are ISO-8601 strings. Post languages and drafts: docs/feed-post-languages-and-drafts.md §4.
 */
import { BetupFeedMatchDto, BetupFeedPlayerDto, BetupFeedTeamDto } from './BetupFeedPostDto';

/** The languages of the localized texts (`name_*` / `description_*` columns); `en` is required, the rest fall back to it. */
export const BETUP_FEED_CHANNEL_LANGUAGES = [
    'en', 'ru', 'ro', 'es', 'it', 'pt', 'de', 'fr', 'tr', 'hi', 'ar', 'uk', 'bg',
] as const;
export type BetupFeedChannelLanguage = typeof BETUP_FEED_CHANNEL_LANGUAGES[number];

/** language → text; the server sends every language (null where unset). */
export type BetupFeedLocalizedTexts = Partial<Record<BetupFeedChannelLanguage, string | null>>;

/** `AdminChannel`. `system_prompt` and `model` are admin-only (never sent to players). */
export interface BetupFeedChannelDto {
    id: number;
    /** The languages the channel writes its posts in (a post's texts must use them); empty / missing = not set yet. */
    languages?: BetupFeedChannelLanguage[] | null;
    names: BetupFeedLocalizedTexts;
    descriptions: BetupFeedLocalizedTexts;
    logo_url: string | null;
    system_prompt: string | null;
    model: string | null;
    category_ids: number[];
    sport_ids: number[];
    /** Inactive = not listed, not searchable, its posts leave the feeds. */
    active: boolean;
    /** Computed by the Crons (§5): `top_pinned` OR more than `feed.channels.top_min_subscribers` subscribers. */
    is_top: boolean;
    /** The operator's "mark as top"; survives the Crons recount. */
    top_pinned: boolean;
    subscribers_count: number;
    posts_count: number;
    created_at: string;
}

/**
 * `POST channels` / `PUT channels/{id}` body. On update, fields left out (or null) keep their value; a blank string
 * clears an optional text field. `names` / `descriptions` are sent whole (every language, null where unset).
 */
export interface BetupFeedChannelRequestDto {
    /** Present = replace the list (at least one). */
    languages?: BetupFeedChannelLanguage[] | null;
    names?: BetupFeedLocalizedTexts | null;
    descriptions?: BetupFeedLocalizedTexts | null;
    logo_url?: string | null;
    system_prompt?: string | null;
    model?: string | null;
    category_ids?: number[] | null;
    sport_ids?: number[] | null;
    active?: boolean | null;
    top_pinned?: boolean | null;
}

/** `AdminChannelCategory` — shown as badges on the channel screen, ordered by `sort_order`. */
export interface BetupFeedChannelCategoryDto {
    id: number;
    /** Stable key (clients filter `GET /channels?category=` by it). */
    code: string;
    names: BetupFeedLocalizedTexts;
    sort_order: number;
    active: boolean;
}

/** `POST channel-categories` / `PUT channel-categories/{id}` body. */
export interface BetupFeedChannelCategoryRequestDto {
    code?: string;
    names: BetupFeedLocalizedTexts;
    sort_order: number;
    active: boolean;
}

/** `GET channels/{id}/posts?status=` — `all` is the admin default. */
export type BetupFeedChannelPostStatus = 'all' | 'draft' | 'scheduled' | 'published';

/** `AdminChannelPost` — `GET channels/{id}/posts` item (newest first, deleted too). */
export interface BetupFeedChannelPostDto {
    id: number;
    /** The English — or first — text (kept for older dashboards). */
    text: string;
    /** Every language the post has; missing on servers before the languages change. */
    texts?: BetupFeedLocalizedTexts | null;
    /** A draft is invisible to players until published. */
    draft?: boolean;
    image_url: string | null;
    match: BetupFeedMatchDto | null;
    team: BetupFeedTeamDto | null;
    /** The player the post names (e.g. congratulates), if any. */
    user: BetupFeedPlayerDto | null;
    likes_count: number;
    comments_count: number;
    /** Full reads by players (Read more in the app); missing on servers before V66. Dashboard only. */
    views_count?: number;
    created_at: string;
    deleted: boolean;
    /** A scheduled draft's publish time (docs/feed-post-scheduling.md). */
    will_publish_at?: string | null;
    channel_id?: number | null;
    /** A premium post's price in betcoins; null = free. */
    price?: number | null;
    sales_count?: number;
    /** Betcoins paid in all. */
    sales_total?: number;
}

/**
 * `POST channels/{id}/posts` body: texts per channel language (at least one, each 1–5000); links a match or a team
 * (not both), and optionally a player; `draft` defaults to false on the server.
 */
export interface BetupFeedChannelPostRequestDto {
    texts: Partial<Record<BetupFeedChannelLanguage, string>>;
    image_url?: string | null;
    match_id?: number | null;
    team_id?: number | null;
    user_id?: number | null;
    draft?: boolean;
}

/**
 * `PUT channels/{id}/posts/{postId}` body — only what changed (absent keeps). `texts`: a key sets that language,
 * `null` removes it (at least one must remain). `image_url` `""` clears; `match_id` / `team_id` / `user_id` `0` removes
 * that link, a positive id sets it (the post must not link both a match and a team).
 */
export interface BetupFeedChannelPostUpdateRequestDto {
    texts?: Partial<Record<BetupFeedChannelLanguage, string | null>>;
    image_url?: string;
    match_id?: number;
    team_id?: number;
    user_id?: number;
}

// ---- Subscriber statistics (betup-server-api docs/feed-channels.md §Subscriber statistics) ----

/** `AdminChannelSubscriberDay`: one day of the period (`date` yyyy-MM-dd in the stats' time zone). */
export interface BetupFeedChannelSubscriberDayDto {
    date: string;
    subscribed: number;
    unsubscribed: number;
    /** Subscribers at the end of the day. */
    total: number;
}

/** A country ISO code, os type or language (`key` null = unknown) and how many of the period's new subscribers. */
export interface BetupFeedChannelSubscriberBreakdownDto {
    key: string | null;
    label: string | null;
    count: number;
}

/** `GET channels/{id}/subscribers/stats?from=&to=&tz=` — whole days `from`..`to` (both included) in `timezone`. */
export interface BetupFeedChannelSubscriberStatsDto {
    channel_id: number;
    from: string;
    to: string;
    timezone: string;
    subscribers_now: number;
    notifications_enabled_now: number;
    total_at_start: number;
    total_at_end: number;
    /** Subscribes in the period (a player who subscribed twice counts twice). */
    subscribed: number;
    unsubscribed: number;
    net: number;
    /** Distinct players who subscribed in the period. */
    new_subscribers: number;
    /** Of them, the ones still subscribed now. */
    still_subscribed: number;
    days: BetupFeedChannelSubscriberDayDto[];
    countries: BetupFeedChannelSubscriberBreakdownDto[];
    platforms: BetupFeedChannelSubscriberBreakdownDto[];
    languages: BetupFeedChannelSubscriberBreakdownDto[];
    /** Since when unsubscribes are recorded; before it only the subscribes of players still subscribed then are known. */
    recorded_since: string | null;
}

/** `GET channels/{id}/subscribers` item — a current subscriber, newest subscription first. */
export interface BetupFeedChannelSubscriberDto {
    user: BetupFeedPlayerDto;
    subscribed_at: string;
    notifications_enabled: boolean;
    registered_at: string | null;
    last_visit: string | null;
    platform: string | null;
    language: string | null;
    /** How many channels the player subscribes to, this one included. */
    channels_count: number;
}

/** `kind` filter of `GET channels/{id}/subscribers/events`. */
export type BetupFeedChannelSubscriberEventKind = 'all' | 'subscribed' | 'unsubscribed';

/** `GET channels/{id}/subscribers/events` item — a subscribe or unsubscribe, newest first. */
export interface BetupFeedChannelSubscriberEventDto {
    user_id: number;
    /** Null when the player no longer exists. */
    user: BetupFeedPlayerDto | null;
    subscribed: boolean;
    at: string;
    /** A subscription that existed before the history was recorded (its subscribe time). */
    backfilled: boolean;
    subscribed_now: boolean;
}

// ---- Channel page (betup-server-api docs/feed-channels.md §Channel page) ----

/** `GET channels/{id}/timeline` — posts newest first; pass `next_cursor` back as `cursor` (null = the last page). */
export interface BetupFeedChannelTimelineDto {
    items: BetupFeedChannelPostDto[];
    next_cursor: string | null;
}

/** `GET channels/{id}/overview` — the channel's figures now. */
export interface BetupFeedChannelOverviewDto {
    channel: BetupFeedChannelDto;
    subscribers: {
        now: number;
        notifications_enabled: number;
        subscribed_7d: number;
        unsubscribed_7d: number;
        subscribed_30d: number;
        unsubscribed_30d: number;
    };
    posts: {
        published: number;
        drafts: number;
        scheduled: number;
        deleted: number;
        published_7d: number;
        published_30d: number;
        premium: number;
        last_published_at: string | null;
        next_scheduled_at: string | null;
    };
    /**
     * Likes / comments / views (full reads, missing on servers before V66) of the live published posts; `_30d` of those
     * published in the last 30 days.
     */
    engagement: { likes: number; comments: number; likes_30d: number; comments_30d: number; views?: number; views_30d?: number };
    /** Premium post purchases (betcoins). */
    sales: { count: number; betcoins: number; buyers: number; count_30d: number; betcoins_30d: number };
    /** The most liked + commented published posts of the last 30 days. */
    top_posts: BetupFeedChannelPostDto[];
    generated_at: string;
}

/** `GET /api/v7/feed/widgets/matches/{id}`'s model — what the `match` and `crowd` widgets draw. */
export interface BetupFeedWidgetMatchDto {
    match: BetupFeedMatchDto;
    /** The main market's open selections (empty when none are open). */
    odds: { label: string; name: string; coefficient: number }[];
    crowd: {
        market_name: string | null;
        bets_count: number;
        players_count: number;
        selections: { label: string; name: string; bets_count: number; share: number }[];
    } | null;
}

/** `GET widgets?match_ids=&user_ids=&team_ids=&lang=` — keyed by id; missing ids are left out. */
export interface BetupFeedWidgetDataDto {
    matches: Record<string, BetupFeedWidgetMatchDto>;
    users: Record<string, BetupFeedPlayerDto>;
    teams: Record<string, { team: BetupFeedTeamDto; sport_name: string | null; next_match: BetupFeedMatchDto | null }>;
}

/** `GET channels/{id}/posts/{postId}/likes` item — who liked a channel post, newest first. */
export interface BetupFeedPostLikeDto {
    user_id: number;
    /** Null when the player no longer exists. */
    user: BetupFeedPlayerDto | null;
    liked_at: string;
}

/**
 * A feed-post comment from betup-server-messaging (`MessageWithReactionsDto`, admin
 * `/api/support/admin/feed/posts/{postId}/comments`): the comment, its reply count and its reactions (likes).
 */
export interface BetupFeedCommentDto {
    message: {
        id: number;
        user: { id: number; name: string | null; avatarUrl: string | null } | null;
        message: string;
        sendDate: string;
    };
    commentCount: number;
    reactions: { count: number; reaction?: { id?: number; name?: string } | null }[] | null;
}

/** Messaging's `CustomPage`: pass `pageOptions.continuationToken` back for the next (older) page. */
export interface BetupFeedCommentPageDto {
    content: BetupFeedCommentDto[];
    numberOfElements: number;
    pageOptions?: { continuationToken?: string | null; pageSize?: number } | null;
}
